import React, { useState, useMemo } from 'react';
import {
  Box, Typography, CircularProgress, TextField, InputAdornment, IconButton, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Tooltip, Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Main from '../layouts/Main';
import { useApi } from '../contexts/Api';
import BrandMetaDialog from '../components/BrandMetaDialog';

const Brands = () => {
  const { brandsMeta, loadingBrandsMeta, fetchBrandsMeta } = useApi();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return brandsMeta;
    return brandsMeta.filter((b) => b.name.toLowerCase().includes(q) || (b.slug || '').includes(q));
  }, [brandsMeta, search]);

  const withMeta = brandsMeta.filter((b) => b.has_meta).length;
  const featured = brandsMeta.filter((b) => b.featured).length;

  return (
    <Main>
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <StarIcon sx={{ fontSize: 22, mr: 1, verticalAlign: 'middle', color: '#845adf' }} />
            Brands
          </h1>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item active" aria-current="page">Brands</li>
          </ol>
        </div>
      </div>

      <Alert severity="info" sx={{ mb: 2 }}>
        Every brand with products already has a live page at <strong>/brands/&#123;slug&#125;</strong>.
        Add a logo and description here to make it rank and look professional. Mark your key brands as
        <strong> Featured</strong> to pin them to the top of the /brands showcase.
      </Alert>

      <div className="row">
        <div className="col-12">
          <div className="card custom-card">
            <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <div className="card-title">All Brands</div>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', ml: 'auto', flexWrap: 'wrap' }}>
                <Chip size="small" label={`${brandsMeta.length} total`} />
                <Chip size="small" label={`${withMeta} with logo/desc`} sx={{ backgroundColor: '#dcfce7', color: '#166534' }} />
                <Chip size="small" icon={<StarIcon sx={{ fontSize: 14 }} />} label={`${featured} featured`} sx={{ backgroundColor: '#fef3c7', color: '#92400e' }} />
                <TextField size="small" placeholder="Search brands…" value={search} onChange={(e) => setSearch(e.target.value)}
                           sx={{ width: 240 }}
                           InputProps={{
                             startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                             endAdornment: search && <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')}><ClearIcon fontSize="small" /></IconButton></InputAdornment>,
                           }} />
              </Box>
            </div>
            <div className="card-body">
              {loadingBrandsMeta ? (
                <Box display="flex" justifyContent="center" py={6}><CircularProgress sx={{ color: '#845adf' }} /></Box>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 64 }}>LOGO</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#6b7280' }}>BRAND</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#6b7280' }}>SLUG</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#6b7280' }} align="center">PRODUCTS</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#6b7280' }} align="center">STATUS</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#6b7280', width: 120 }} align="center">ACTIONS</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filtered.map((b) => (
                        <TableRow key={b.marka_id} hover>
                          <TableCell>
                            <Box sx={{ width: 44, height: 44, borderRadius: 1, backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                              {b.logo_url
                                ? <img src={b.logo_url} alt={b.name} style={{ maxWidth: '85%', maxHeight: '85%', objectFit: 'contain' }} />
                                : <Typography variant="caption" sx={{ color: '#c4c9d0', fontWeight: 700, fontSize: 11 }}>{b.name.slice(0, 2).toUpperCase()}</Typography>}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{b.name}</Typography>
                              {b.featured ? <StarIcon sx={{ fontSize: 15, color: '#f59e0b' }} /> : null}
                            </Box>
                          </TableCell>
                          <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#6b7280' }}>{b.slug}</Typography></TableCell>
                          <TableCell align="center"><Chip size="small" label={b.count} variant="outlined" /></TableCell>
                          <TableCell align="center">
                            {!b.active
                              ? <Chip size="small" label="Hidden" sx={{ backgroundColor: '#fee2e2', color: '#991b1b', fontWeight: 600 }} />
                              : b.has_meta
                                ? <Chip size="small" icon={<CheckCircleIcon sx={{ fontSize: 14 }} />} label="Complete" sx={{ backgroundColor: '#dcfce7', color: '#166534', fontWeight: 600, '& .MuiChip-icon': { color: '#16a34a' } }} />
                                : <Chip size="small" label="No content" sx={{ backgroundColor: '#f3f4f6', color: '#6b7280' }} />}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Open live page">
                              <IconButton size="small" component="a" href={`https://elnaf.fi/en/brands/${b.slug}`} target="_blank"><OpenInNewIcon fontSize="small" /></IconButton>
                            </Tooltip>
                            <Button size="small" startIcon={<EditIcon />} onClick={() => setEditing(b)}
                                    sx={{ textTransform: 'none', color: '#845adf' }}>Edit</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {!filtered.length && (
                        <TableRow><TableCell colSpan={6}>
                          <Box textAlign="center" py={5}><Typography variant="body2" color="text.secondary">No brands match this search.</Typography></Box>
                        </TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </div>
          </div>
        </div>
      </div>

      <BrandMetaDialog
        open={!!editing}
        brand={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); fetchBrandsMeta(); }}
      />
    </Main>
  );
};

export default Brands;
